import { SegmentType } from '@ve-pro/enums/segment'
import { TrackType } from '@ve-pro/enums/track-type'
import { useTrackStore } from '@ve-pro/store'
import {
  getGroupBySegment,
  isSegmentGroupChild,
} from '@ve-pro/store/tracks/segment/segment-group'
import type { SegmentCollection, Track } from '@ve-pro/types/track.type'

enum TrackHeightSize {
  medium = 64,
  small = 40,
  mini = 24,
}

export const TrackHeightConfig: Record<
  TrackType,
  { collapse?: number; expand: number }
> = {
  text: { expand: TrackHeightSize.mini },
  sticker: { expand: TrackHeightSize.mini },
  filter: { expand: TrackHeightSize.mini },
  video: { expand: TrackHeightSize.medium, collapse: TrackHeightSize.mini },
  audio: { expand: TrackHeightSize.small, collapse: TrackHeightSize.mini },
}
interface SegmentTrackMapType {
  [SegmentType.Audio]: TrackType.Audio
  [SegmentType.Filter]: TrackType.Filter
  [SegmentType.Sticker]: TrackType.Sticker
  [SegmentType.Text]: TrackType.Text
  [SegmentType.SubtitleGroup]: TrackType.Text
  [SegmentType.Video]: TrackType.Video
  [SegmentType.Transition]: TrackType.Video
  [SegmentType.Fragment]: TrackType.Video
}
export const SegmentTrackMap: SegmentTrackMapType = {
  [SegmentType.Audio]: TrackType.Audio,
  [SegmentType.Filter]: TrackType.Filter,
  [SegmentType.Sticker]: TrackType.Sticker,
  [SegmentType.Text]: TrackType.Text,
  [SegmentType.SubtitleGroup]: TrackType.Text,
  [SegmentType.Video]: TrackType.Video,
  [SegmentType.Transition]: TrackType.Video,
  [SegmentType.Fragment]: TrackType.Video,
}

/**
 * 获取轨道的高度（px）
 * @param type 轨道类型
 * @returns 轨道高度
 */
export function getTrackHeight(type: TrackType): number {
  const trackStore = useTrackStore()
  const collapse = trackStore.collapseAudioTrack
  const item = TrackHeightConfig[type]
  if (!item) {
    return TrackHeightSize.mini
  }

  return collapse && type === TrackType.Audio && item.collapse
    ? item.collapse
    : item.expand
}
export function getTrackHeightMature(track: Track): number {
  const trackStore = useTrackStore()

  return trackStore.collapseTracks.some((t) => t.id === track.id)
    ? TrackHeightConfig[TrackType.Video].collapse ?? getTrackHeight(track.type)
    : getTrackHeight(track.type)
}

/**
 * 获取元件的高度（px）
 * @param segment 元件
 * @returns 元件高度
 */
export function getTrackSegmentHeight(
  segment: SegmentCollection,
): ReturnType<typeof getTrackHeightMature> {
  const trackStore = useTrackStore()
  segment = isSegmentGroupChild(segment)
    ? getGroupBySegment(segment) ?? segment
    : segment

  const track = trackStore.tracks.find((t) => t.id === segment.track_id)
  return track
    ? getTrackHeightMature(track)
    : getTrackHeight(SegmentTrackMap[segment.type])
}

// 轨道间距
export const TRACK_GAP = 4 // px

export function getTrackTypeBySegmentType(type: SegmentType): TrackType {
  return SegmentTrackMap[type]
}
